<template>
    <ChatAgentToolFrame v-if="isVisible" class="mt-4 w-full place-self-start gap-0 p-0 shadow-md rounded-2xl md:w-full mb-5" data-testid="proactive-coaching-offer">
        <div class="relative flex flex-col gap-4 px-6 pt-6 pb-5">
            <button
                type="button"
                aria-label="Dismiss coaching offer"
                class="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full text-valence-grey-500 transition-colors hover:bg-valence-grey-100 hover:text-valence-grey-750"
                :disabled="isBusy"
                @click="handleDismiss">
                <i class="bi bi-x-lg text-sm/none" aria-hidden="true"></i>
            </button>

            <div class="scaffold-details-small flex w-fit items-center gap-2 rounded-full bg-valence-green-olive-light px-3 py-1 text-valence-grey-750">
                <span class="h-2 w-2 rounded-full bg-valence-green-olive" aria-hidden="true"></span>
                Coaching moment
            </div>

            <div class="flex flex-col gap-3 pr-4">
                <h3 class="nadia-subtitle text-valence-grey-850">{{ data.action_params.card_header }}</h3>
                <p class="body-small text-valence-grey-600">{{ data.action_params.card_subtext }}</p>
            </div>
        </div>

        <div class="border-t border-valence-grey-200 bg-valence-grey-50 px-6 py-3 rounded-b-2xl">
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                    <span class="scaffold-details-small text-valence-grey-500">Helpful?</span>
                    <div class="flex items-center gap-1">
                        <button
                            type="button"
                            aria-label="Helpful proactive coaching offer"
                            :aria-pressed="currentFeedback === 'up'"
                            :disabled="isBusy"
                            :class="feedbackButtonClass(currentFeedback === 'up')"
                            @click="handleFeedback('up')">
                            <ThumbsUp aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            aria-label="Not helpful proactive coaching offer"
                            :aria-pressed="currentFeedback === 'down'"
                            :disabled="isBusy"
                            :class="feedbackButtonClass(currentFeedback === 'down')"
                            @click="handleFeedback('down')">
                            <ThumbsDown aria-hidden="true" />
                        </button>
                    </div>
                </div>
                <!-- FIXME: temporary override of class to resemble new button theme -->
                <BaseButton theme="primary" size="sm" data-testid="proactive-offer-cta" class="rounded-lg bg-[#3b3b3b] px-4 py-2" :disabled="isBusy" @click="handleStartChat">
                    <span class="flex items-center gap-2">
                        <LoadingSpinner v-if="isLoading" class="h-4 w-4" />
                        Let's chat
                    </span>
                </BaseButton>
            </div>
            <p v-if="errorMessage" role="alert" class="details text-red-700">{{ errorMessage }}</p>
        </div>
    </ChatAgentToolFrame>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import ThumbsDown from "~vue/icons/ThumbsDown.vue";
import ThumbsUp from "~vue/icons/ThumbsUp.vue";
import type { ParseActionsProactiveCoachingOfferAction, UpdateActionState } from "~vue/types/chat";
import { openUrl } from "~vue/utils";
import { logError } from "~vue/utils/logUtils";
import { computed, inject, ref } from "vue";

import ChatAgentToolFrame from "./ChatAgentToolFrame.vue";

interface Props {
    data: ParseActionsProactiveCoachingOfferAction;
    lineIdx: number;
    messageId: number;
}

type ActionResult = {
    success: boolean;
    payload?: {
        coaching_session_url?: string;
        feedback?: OfferFeedback | null;
        error?: string;
    };
};

const props = defineProps<Props>();
const { execute } = useActionHandler();
const updateActionState = inject<UpdateActionState>("updateActionState")!;
const isLoading = ref(false);
const isFeedbackLoading = ref(false);
const errorMessage = ref("");
const defaultActionState = { dismissed: false, minimized: false, submitted: false };
const genericErrorMessage = "Something went wrong. Please try again.";

const isVisible = computed(() => !props.data.action_state?.dismissed && !props.data.action_state?.submitted);
const currentActionState = computed(() => props.data.action_state ?? defaultActionState);
type OfferFeedback = Exclude<ParseActionsProactiveCoachingOfferAction["action_params"]["feedback"], null | undefined>;
const currentFeedback = computed(() => props.data.action_params.feedback ?? null);
const isBusy = computed(() => isLoading.value || isFeedbackLoading.value);

const feedbackButtonClass = (active: boolean) => [
    "flex h-8 w-8 items-center justify-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-50",
    active ? "border-valence-primary-500/40 bg-valence-primary-100" : "border-transparent hover:border-valence-grey-300 hover:bg-valence-grey-100",
];

async function handleDismiss() {
    if (isLoading.value) {
        return;
    }
    const previousActionState = { ...currentActionState.value };
    isLoading.value = true;
    try {
        const result: ActionResult = await execute(
            props.data.action_name,
            {
                message_id: props.messageId,
                lineIdx: props.lineIdx,
                action_state: { ...previousActionState, dismissed: true },
            },
            { sync: true },
        );
        if (!result?.success) {
            updateActionState({ message_id: props.messageId, lineIdx: props.lineIdx, action_state: previousActionState });
            errorMessage.value = genericErrorMessage;
            return;
        }
    } catch (error) {
        logError(error);
        updateActionState({ message_id: props.messageId, lineIdx: props.lineIdx, action_state: previousActionState });
        errorMessage.value = genericErrorMessage;
    } finally {
        isLoading.value = false;
    }
}

async function handleStartChat() {
    if (isLoading.value) {
        return;
    }
    isLoading.value = true;
    errorMessage.value = "";
    try {
        const result: ActionResult = await execute(
            props.data.action_name,
            {
                message_id: props.messageId,
                lineIdx: props.lineIdx,
                subaction: "start_chat",
            },
            { sync: true, optimistic: false },
        );
        const destination = result?.payload?.coaching_session_url;
        if (!result?.success || !destination) {
            errorMessage.value = genericErrorMessage;
            return;
        }
        updateActionState({
            message_id: props.messageId,
            lineIdx: props.lineIdx,
            action_state: { ...currentActionState.value, submitted: true },
        });
        openUrl(destination);
    } catch (error) {
        logError(error);
        errorMessage.value = genericErrorMessage;
    } finally {
        isLoading.value = false;
    }
}

async function handleFeedback(feedback: OfferFeedback) {
    if (isBusy.value) {
        return;
    }
    const previousFeedback = currentFeedback.value;
    const nextFeedback = previousFeedback === feedback ? null : feedback;
    isFeedbackLoading.value = true;
    errorMessage.value = "";
    try {
        const result: ActionResult = await execute(
            props.data.action_name,
            {
                message_id: props.messageId,
                lineIdx: props.lineIdx,
                subaction: "rate",
                action_params: { feedback: nextFeedback },
            },
            { sync: true, optimistic: false },
        );
        if (!result?.success) {
            errorMessage.value = genericErrorMessage;
            return;
        }
        updateActionState({
            message_id: props.messageId,
            lineIdx: props.lineIdx,
            action_params: { feedback: result.payload?.feedback ?? null },
        });
    } catch (error) {
        logError(error);
        errorMessage.value = genericErrorMessage;
    } finally {
        isFeedbackLoading.value = false;
    }
}
</script>
